import { createClient } from '@supabase/supabase-js';

export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  {
    auth: {
      // Implicit flow puts the recovery/confirmation token directly in the URL
      // fragment, so password-reset links work even when the link is opened in
      // a different browser or device than it was requested from. PKCE (the
      // default) can't do this — it needs a code verifier stored locally on the
      // requesting device, which isn't there when the email link opens in the
      // system browser. That mismatch is what leaves the reset page hanging.
      flowType: 'implicit',
      detectSessionInUrl: true,
      persistSession: true,
      autoRefreshToken: true,
    },
  }
);

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };
}

async function get(path) {
  const res = await fetch(`${BASE}${path}`, { headers: await authHeaders() });
  if (!res.ok) throw await res.json();
  return res.json();
}

async function post(path, body) {
  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers: await authHeaders(), body: JSON.stringify(body) });
  if (!res.ok) throw await res.json();
  return res.json();
}

async function del(path, body) {
  const opts = { method: 'DELETE', headers: await authHeaders() };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(`${BASE}${path}`, opts);
  if (!res.ok) throw await res.json();
  return res.json();
}

export const api = {
  // Auth
  getMe: ()                  => get('/auth/me'),

  // Wallet
  getWallet: ()              => get('/wallet'),

  // Packs
  getPacks: ()               => get('/packs'),
  purchasePack: (body)       => post('/packs/purchase', body),
  finalizePack: (body)       => post('/packs/purchase/finalize', body),

  // Treatments
  getTreatments: ()          => get('/treatments'),

  // Slots
  getSlots: (resourceType, from, to) => get(`/slots?resourceType=${resourceType}&from=${from}${to ? `&to=${to}` : ''}`),
  getBiohackAvailability: (date, type) => get(`/slots/biohack-availability?date=${date}&type=${type}`),

  // Bookings
  getBookings: ()            => get('/bookings'),
  createBooking: (body)      => post('/bookings', body),
  finalizeBooking: (body)    => post('/bookings/finalize', body),
  cancelBooking: (id)        => del(`/bookings/${id}`),

  // Biohack
  createBiohack: (body)      => post('/biohack', body),
  cancelBiohack: (id)        => del(`/biohack/${id}`),

  // Push
  sendWelcomePush: ()        => post('/push/welcome', {}),
  registerNativeToken: (body) => post('/push/native-token', body),
  removeNativeToken: (body)   => del('/push/native-token', body),

  // TeamUp (class timetable + booking)
  getTeamupClasses: (from, to) => get(`/teamup/classes${from || to ? `?${[from && `from=${encodeURIComponent(from)}`, to && `to=${encodeURIComponent(to)}`].filter(Boolean).join('&')}` : ''}`),
  getTeamupBookings: () => get('/teamup/bookings'),
  getTeamupMembershipStatus: () => get('/teamup/membership-status'),
  bookTeamupClass: (eventId, details = {}) => post('/teamup/book', { eventId, ...details }),
  cancelTeamupBooking: (eventId) => del(`/teamup/bookings/${eventId}`),
};
